// Mesai Takip Sayfası
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  Button,
  Chip,
  LinearProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  Pause,
  PlayCircleOutline,
  Refresh,
  CheckCircle,
  Warning,
  Info
} from '@mui/icons-material';
import { useWorkTime } from '../contexts/WorkTimeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { WorkTimeStatus, WorkTimeSummary } from '../types';

const WorkTime: React.FC = () => {
  const { user } = useAuth();
  const { settings } = useSettings();
  const { mode } = useCustomTheme();
  const {
    currentSession,
    workTimeStatus,
    isLoading,
    startWork,
    endWork,
    startBreak,
    endBreak,
    getDailySummary,
    getWorkTimeHistory,
    formatDuration,
    getStatusText,
  } = useWorkTime();

  const [dailySummary, setDailySummary] = useState<WorkTimeSummary | null>(null);
  const [workHistory, setWorkHistory] = useState<WorkTimeSummary[]>([]);
  const [selectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [startDate, setStartDate] = useState(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [isNotesDialogOpen, setIsNotesDialogOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [location, setLocation] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false, message: '', severity: 'info'
  });

  // Günlük özeti yükle
  useEffect(() => {
    loadDailySummary();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Mesai geçmişini yükle
  useEffect(() => {
    loadWorkHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const loadDailySummary = async () => {
    const summary = await getDailySummary(selectedDate);
    setDailySummary(summary);
  };

  const loadWorkHistory = async () => {
    const history = await getWorkTimeHistory(startDate, endDate);
    setWorkHistory(history);
  };

  const handleWorkStart = async () => {
    setIsNotesDialogOpen(true);
  };

  const handleWorkEnd = async () => {
    const success = await endWork(notes);
    if (success) {
      setSnackbar({ open: true, message: 'Mesai başarıyla bitti!', severity: 'success' });
      loadDailySummary();
    } else {
      setSnackbar({ open: true, message: 'Mesai bitirme hatası!', severity: 'error' });
    }
    setIsNotesDialogOpen(false);
    setNotes('');
  };

  const handleBreakStart = async () => {
    const success = await startBreak(notes);
    if (success) {
      setSnackbar({ open: true, message: 'Mola başlatıldı!', severity: 'success' });
      loadDailySummary();
    } else {
      setSnackbar({ open: true, message: 'Mola başlatma hatası!', severity: 'error' });
    }
    setIsNotesDialogOpen(false);
    setNotes('');
  };

  const handleBreakEnd = async () => {
    const success = await endBreak(notes);
    if (success) {
      setSnackbar({ open: true, message: 'Mola bitti!', severity: 'success' });
      loadDailySummary();
    } else {
      setSnackbar({ open: true, message: 'Mola bitirme hatası!', severity: 'error' });
    }
    setIsNotesDialogOpen(false);
    setNotes('');
  };

  const handleConfirmAction = async () => {
    if (workTimeStatus === WorkTimeStatus.OFF_DUTY) {
      const success = await startWork(notes, location);
      if (success) {
        setSnackbar({ open: true, message: 'Mesai başlatıldı!', severity: 'success' });
        loadDailySummary();
      } else {
        setSnackbar({ open: true, message: 'Mesai başlatma hatası!', severity: 'error' });
      }
    }
    setIsNotesDialogOpen(false);
    setNotes('');
    setLocation('');
  };

  const getStatusIcon = (status: WorkTimeStatus) => {
    switch (status) {
      case WorkTimeStatus.WORKING:
        return <PlayArrow color="success" />;
      case WorkTimeStatus.ON_BREAK:
        return <Pause color="warning" />;
      case WorkTimeStatus.OFF_DUTY:
        return <Stop color="error" />;
      default:
        return <Info />;
    }
  };

  const getActionButtons = () => {
    switch (workTimeStatus) {
      case WorkTimeStatus.OFF_DUTY:
        return (
          <Button
            variant="contained"
            color="success"
            startIcon={<PlayArrow />}
            onClick={handleWorkStart}
            disabled={isLoading}
            size="large"
          >
            Mesai Başlat
          </Button>
        );
      case WorkTimeStatus.WORKING:
        return (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="warning"
              startIcon={<Pause />}
              onClick={handleBreakStart}
              disabled={isLoading}
            >
              Mola Başlat
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<Stop />}
              onClick={handleWorkEnd}
              disabled={isLoading}
            >
              Mesai Bitir
            </Button>
          </Box>
        );
      case WorkTimeStatus.ON_BREAK:
        return (
          <Button
            variant="contained"
            color="success"
            startIcon={<PlayCircleOutline />}
            onClick={handleBreakEnd}
            disabled={isLoading}
            size="large"
          >
            Mola Bitir
          </Button>
        );
      default:
        return null;
    }
  };

  if (!user) {
    return (
      <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
        <Alert severity="error">
          Bu sayfaya erişim için giriş yapmanız gerekiyor.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 3 }}>
        {/* Başlık ve Durum */}
        <Card sx={{ p: 3, bgcolor: mode === 'dark' ? settings.darkModeCardBackground : settings.lightModeCardBackground, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" component="h2" sx={{ color: mode === 'dark' ? settings.darkModeTextColor : settings.lightModeTextColor }}>
              Mesai Takip
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                icon={getStatusIcon(workTimeStatus)}
                label={getStatusText(workTimeStatus)}
                color={workTimeStatus === WorkTimeStatus.WORKING ? 'success' : 
                       workTimeStatus === WorkTimeStatus.ON_BREAK ? 'warning' : 'error'}
                variant="filled"
              />
              <IconButton onClick={loadDailySummary} disabled={isLoading}>
                <Refresh />
              </IconButton>
            </Box>
          </Box>
          
          {isLoading && <LinearProgress sx={{ mb: 2 }} />}
          
          {/* Mevcut Session Bilgileri */}
          {currentSession && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Başlangıç:</strong> {currentSession.startTime.toLocaleString('tr-TR')}
                {currentSession.breakStartTime && (
                  <>
                    <br />
                    <strong>Mola Başlangıcı:</strong> {currentSession.breakStartTime.toLocaleString('tr-TR')}
                  </>
                )}
                <br />
                <strong>Toplam Çalışma:</strong> {formatDuration(currentSession.totalWorkMinutes)}
                <br />
                <strong>Toplam Mola:</strong> {formatDuration(currentSession.totalBreakMinutes)}
              </Typography>
            </Alert>
          )}
          
          {/* Aksiyon Butonları */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            {getActionButtons()}
          </Box>
        </Card>

        {/* Günlük Özet */}
        <Card sx={{ p: 3, bgcolor: mode === 'dark' ? settings.darkModeCardBackground : settings.lightModeCardBackground, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: mode === 'dark' ? settings.darkModeTextColor : settings.lightModeTextColor }}>
            Günlük Özet - {new Date(selectedDate).toLocaleDateString('tr-TR')}
          </Typography>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, 
            gap: 2 
          }}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'grey.50' }}>
              <Typography variant="h4" color="primary">
                {dailySummary ? formatDuration(dailySummary.totalWorkMinutes) : '00:00'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Toplam Çalışma
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'grey.50' }}>
              <Typography variant="h4" color="warning.main">
                {dailySummary ? formatDuration(dailySummary.totalBreakMinutes) : '00:00'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Toplam Mola
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'grey.50' }}>
              <Typography variant="h4" color="success.main">
                {dailySummary ? dailySummary.sessions.length : 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Session Sayısı
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'grey.50' }}>
              <Typography variant="h4" color={dailySummary?.isComplete ? 'success.main' : 'error.main'}>
                {dailySummary?.isComplete ? '✓' : '✗'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Gün Tamamlandı
              </Typography>
            </Paper>
          </Box>
        </Card>

        {/* Mesai Geçmişi */}
        <Card sx={{ p: 3, bgcolor: mode === 'dark' ? settings.darkModeCardBackground : settings.lightModeCardBackground, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: mode === 'dark' ? settings.darkModeTextColor : settings.lightModeTextColor }}>
              Mesai Geçmişi
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                type="date"
                label="Başlangıç"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                label="Bitiş"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadWorkHistory}
                disabled={isLoading}
              >
                Yenile
              </Button>
            </Box>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tarih</TableCell>
                  <TableCell align="right">Çalışma Süresi</TableCell>
                  <TableCell align="right">Mola Süresi</TableCell>
                  <TableCell align="right">Session Sayısı</TableCell>
                  <TableCell align="center">Durum</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workHistory.map((summary, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {new Date(summary.date).toLocaleDateString('tr-TR')}
                    </TableCell>
                    <TableCell align="right">
                      {formatDuration(summary.totalWorkMinutes)}
                    </TableCell>
                    <TableCell align="right">
                      {formatDuration(summary.totalBreakMinutes)}
                    </TableCell>
                    <TableCell align="right">
                      {summary.sessions.length}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={summary.isComplete ? <CheckCircle /> : <Warning />}
                        label={summary.isComplete ? 'Tamamlandı' : 'Devam Ediyor'}
                        color={summary.isComplete ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>

      {/* Notlar Dialog */}
      <Dialog open={isNotesDialogOpen} onClose={() => setIsNotesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {workTimeStatus === WorkTimeStatus.OFF_DUTY ? 'Mesai Başlat' : 
           workTimeStatus === WorkTimeStatus.WORKING ? 'Mesai Bitir' :
           workTimeStatus === WorkTimeStatus.ON_BREAK ? 'Mola Bitir' : 'Not Ekle'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {workTimeStatus === WorkTimeStatus.OFF_DUTY && (
              <TextField
                fullWidth
                label="Çalışma Yeri"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Ev ofis, şirket, vb."
              />
            )}
            <TextField
              fullWidth
              label="Notlar (Opsiyonel)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={3}
              placeholder="Mesai hakkında notlarınızı yazın..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsNotesDialogOpen(false)}>İptal</Button>
          <Button
            variant="contained"
            onClick={handleConfirmAction}
            disabled={isLoading}
          >
            {workTimeStatus === WorkTimeStatus.OFF_DUTY ? 'Mesai Başlat' : 'Onayla'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default WorkTime;
